import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {mustMatch} from '@shared/validator/must-match';
import {RoutingService} from '@app/service/routing/routing.service';
import {LoggerService} from '@app/service/logger/logger.service';
import {Store} from '@ngxs/store';
import {
  RegisterAnonToEmail,
  Register,
  GoogleLogin, FacebookLogin
} from '@shared/state/auth/auth.actions';
import {ActivatedRoute} from '@angular/router';
import {ToastService} from '@app/service/util/toast.service';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {Meta, Title} from '@angular/platform-browser';
import {AuthState} from '@shared/state/auth/auth.state';
import {setSEOInfo} from '@shared/helpers/seo';
import {faFacebookF} from '@fortawesome/free-brands-svg-icons';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss', './../auth.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterComponent implements OnInit, OnDestroy {
  submitted: boolean;
  messages: string[];
  registerForm: FormGroup;
  passwordVisible = false;
  faFacebook = faFacebookF;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  redirectLink;

  constructor(private routingService: RoutingService, private ngZone: NgZone, private activatedRoute: ActivatedRoute,
              private fb: FormBuilder, protected cd: ChangeDetectorRef, private logger: LoggerService,
              private store: Store, private toastr: ToastService, private title: Title, private meta: Meta) {
    const pageName = 'Register Carvry Account';
    const desc = 'Register to explore top vehicles and save searches you make';
    setSEOInfo(this.title, this.meta, pageName, desc);
  }

  ngOnInit() {
    this.messages = [];
    this.submitted = false;

    this.registerForm = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, {
      validator: mustMatch('password', 'confirmPassword')
    });
    this.resolveNavParams();
  }

  resolveNavParams() {
    this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe(params => {
        if (!params) {
          return;
        }
        const email = params.email;
        const redirectLink = params.redirectTo;
        if (email) {
          this.f.email.setValue(email);
        }
        if (redirectLink) {
          this.redirectLink = redirectLink;
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get f() {
    return this.registerForm.controls;
  }

  register(): any {
    this.messages = [];
    this.submitted = true;

    // stop here if form is invalid
    if (this.registerForm.invalid) {
      this.toastr.error('Some field(s) are not correct in the form!');
      return;
    }

    const data = this.registerForm.value;
    const userData: Partial<any> | any = {
      email: data.email,
      name: data.fullName
    };
    const shouldLinkAnon = this.store.selectSnapshot(AuthState.getIsAnonymous);
    if (shouldLinkAnon) {
      return this.store.dispatch(new RegisterAnonToEmail({
        email: data.email,
        password: data.password,
        linkType: 'register',
        redirectLink: this.redirectLink,
        ...userData
      }))
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          setTimeout(() => {
            this.submitted = false;
            this.registerForm.reset();

          }, 500);
        });
    } else {
      return this.store.dispatch(new Register({
        email: data.email,
        password: data.password,
        redirectLink: this.redirectLink,
        ...userData
      }))
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          setTimeout(() => {
            this.submitted = false;
            this.registerForm.reset();
          }, 500);
        });
    }
  }

  loginWithGoogle() {
    this.submitted = true;
    this.store.dispatch(new GoogleLogin({
      linkType: 'login',
      redirectLink: this.redirectLink
    }));
  }

  loginWithFacebook() {
    this.submitted = true;
    this.store.dispatch(new FacebookLogin({
      linkType: 'login',
      redirectLink: this.redirectLink
    }));
  }
}
